export interface FitBox {
  top: string;
  left: string;
  width: string;
  height: string;
}

// Matches the on-canvas footprint of the generic default box (300x200) this
// replaces, so components sized via this utility don't appear conspicuously
// larger or smaller than components placed the old way.
export const FIT_BOX_TARGET_AREA = 300 * 200;

export interface Point {
  x: number;
  y: number;
}

// Fits a box preserving `width`/`height`'s aspect ratio at `targetArea`
// on-canvas footprint. Centered on `anchorCenter` when given (clamped so
// the box stays fully on-canvas), otherwise centered on the canvas. Never
// scales up — only shrinks, preserving aspect ratio, when the target
// footprint would otherwise overflow the canvas. Returns null when
// width/height can't produce a valid box.
export function computeFitBox(
  width: number,
  height: number,
  targetArea: number,
  canvasWidth: number,
  canvasHeight: number,
  anchorCenter?: Point,
): FitBox | null {
  if (!width || !height) {
    return null;
  }

  const aspectRatio = width / height;
  const targetHeight = Math.sqrt(targetArea / aspectRatio);
  const targetWidth = targetHeight * aspectRatio;

  const scale = Math.min(
    1,
    canvasWidth / targetWidth,
    canvasHeight / targetHeight,
  );
  const fittedWidth = targetWidth * scale;
  const fittedHeight = targetHeight * scale;

  const centerX = anchorCenter?.x ?? canvasWidth / 2;
  const centerY = anchorCenter?.y ?? canvasHeight / 2;

  const left = clamp(centerX - fittedWidth / 2, 0, canvasWidth - fittedWidth);
  const top = clamp(centerY - fittedHeight / 2, 0, canvasHeight - fittedHeight);

  return {
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    width: `${Math.round(fittedWidth)}px`,
    height: `${Math.round(fittedHeight)}px`,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// The subset of a layout's metadata (from html-layout-library's
// metadata.json) this module needs. `repeating` only describes HOW a layout repeats (e.g. counter
// blocks tiling horizontally) — it carries no count. The caller supplies
// `repeatCount` itself (see computeLayoutHugBox).
export interface LayoutMetadataForFit {
  baseWidth: number;
  baseHeight: number;
  enforceBaseMinimum?: boolean;
  repeating?: { countProperty: string; direction: string };
}

// Combines a layout's base dimensions (from html-layout-library's
// metadata.json) with the caller's current repeat count into a hug box.
// Shared by both add-time sizing (UserComponentsService) and switch-time
// sizing (each layout-*.component.ts's setLayout()), so the
// enforceBaseMinimum/repeating handling only lives in one place.
//
// `anchorCenter` lets a switch-time caller (LayoutHugBoxService) keep the
// box centered where the user already positioned it, instead of resetting
// to canvas-center on every switch — see computeFitBox.
//
// `repeatCount` is ignored unless `metadata.repeating` is set — a
// non-repeating layout's baseWidth is already the whole layout's width.
// When it IS set, `repeatCount` must be the caller's own current count
// (e.g. Countdown's visible block count, Weather's daysToShow); this
// function has no opinion on what that count should default to.
//
// `enforceBaseMinimum` mirrors the same field in the layout's own
// LAYOUT_METADATA: when true, the layout's runtime (LayoutScaleManager, in
// html-layout-library's common/js) refuses to render below its native
// footprint — it forces scale back up to 1 and crops/clips instead of
// shrinking. So the target area here must never go below that footprint,
// even though the generic FIT_BOX_TARGET_AREA would otherwise ask for a
// smaller box.
export function computeLayoutHugBox(
  metadata: LayoutMetadataForFit | null | undefined,
  repeatCount: number,
  canvasWidth: number,
  canvasHeight: number,
  anchorCenter?: Point,
): FitBox | null {
  if (!metadata) {
    return null;
  }

  const { baseWidth, baseHeight, enforceBaseMinimum, repeating } = metadata;

  const isValidDimension = (value: unknown): value is number =>
    typeof value === 'number' && isFinite(value) && value > 0;

  if (!isValidDimension(baseWidth) || !isValidDimension(baseHeight)) {
    return null;
  }

  // `direction` picks which axis the repeat count multiplies. Two layouts
  // can share the same baseWidth/baseHeight and only differ in direction
  // (e.g. weather-horizontal vs weather-vertical, both 136x71) — always
  // multiplying width regardless of direction would make those layouts
  // produce an identical box.
  const clampedRepeatCount = Math.max(1, repeatCount || 1);
  const repeatsVertically = repeating?.direction === 'vertical';
  const effectiveWidth =
    repeating && !repeatsVertically
      ? baseWidth * clampedRepeatCount
      : baseWidth;
  const effectiveHeight =
    repeating && repeatsVertically
      ? baseHeight * clampedRepeatCount
      : baseHeight;

  const targetArea = enforceBaseMinimum
    ? Math.max(FIT_BOX_TARGET_AREA, effectiveWidth * effectiveHeight)
    : FIT_BOX_TARGET_AREA;

  return computeFitBox(
    effectiveWidth,
    effectiveHeight,
    targetArea,
    canvasWidth,
    canvasHeight,
    anchorCenter,
  );
}
